import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Firebase storage service for file uploads
export const uploadFile = (file, folder, onProgress, onError, onComplete) => {
  try {
    // Get storage instance
    const storage = getStorage();
    
    // Create storage reference with timestamp to avoid naming conflicts
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${folder}/${fileName}`);
    
    // Create upload task
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    // Monitor upload progress
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Calculate progress
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(progress);
        }
      },
      (error) => {
        // Handle upload error
        console.error('Upload error:', error);
        if (onError) {
          onError(error);
        }
      },
      () => {
        // Upload completed successfully
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          if (onComplete) {
            onComplete(downloadURL);
          }
        }).catch((error) => {
          console.error('Error getting download URL:', error);
          if (onError) {
            onError(error);
          }
        });
      }
    );
    
    return uploadTask;
  } catch (error) {
    console.error('Error starting upload:', error);
    if (onError) {
      onError(error);
    }
  }
};

// Helper function for uploading with promise-based approach
export const uploadFileAsync = (file, folder) => {
  return new Promise((resolve, reject) => {
    uploadFile(
      file,
      folder,
      (progress) => {
        // Progress callback (optional)
        console.log(`Upload progress: ${progress}%`);
      },
      (error) => {
        reject(error);
      },
      (downloadURL) => {
        resolve(downloadURL);
      }
    );
  });
};

// Helper function to validate file types
export const validateFileType = (file, allowedTypes) => {
  if (!file) return { valid: false, error: 'No file selected' };
  
  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` 
    };
  }
  
  return { valid: true };
};

// Helper function to validate file size
export const validateFileSize = (file, maxSizeInMB) => {
  if (!file) return { valid: false, error: 'No file selected' };
  
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  
  if (file.size > maxSizeInBytes) {
    return { 
      valid: false, 
      error: `File size exceeds ${maxSizeInMB}MB limit` 
    };
  }
  
  return { valid: true };
};

const firebaseStorageService = {
  uploadFile,
  uploadFileAsync,
  validateFileType,
  validateFileSize
};

export default firebaseStorageService;
